import { useState } from "react";
import { getBranchingStats, type BranchStat } from "@/lib/analyticsDB";
import type { AnalyticsSession } from "@/lib/analyticsDB";

const QUESTION_ICONS: Record<string, string> = {
  industry: "ri-briefcase-line",
  teamSize: "ri-group-line",
  challenges: "ri-task-line",
  workMode: "ri-home-office-line",
  currentTools: "ri-tools-line",
  complianceNeeds: "ri-shield-check-line",
  locationCount: "ri-map-pin-line",
};

const BRANCH_COLORS: Record<string, string> = {
  industry: "#10b981",
  teamSize: "#059669",
  challenges: "#34d399",
  workMode: "#14b8a6",
  currentTools: "#0ea5e9",
  complianceNeeds: "#8b5cf6",
  locationCount: "#f59e0b",
};

function StatBadge({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="glass-card p-3 flex items-center gap-3">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}
      >
        <span className="text-sm font-bold" style={{ color }}>{value}</span>
      </div>
      <span className="text-xs text-silver-300">{label}</span>
    </div>
  );
}

function QuestionRow({ stat, totalSessions, isBranch }: { stat: BranchStat; totalSessions: number; isBranch: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const shownPct = totalSessions > 0 ? Math.round((stat.shownCount / totalSessions) * 100) : 0;
  const answeredPctOfShown = stat.shownCount > 0 ? Math.round((stat.answeredCount / stat.shownCount) * 100) : 0;
  const color = BRANCH_COLORS[stat.questionId] || "#10b981";

  return (
    <div className="border-b border-graphite-700/30 last:border-0">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 py-3.5 text-left cursor-pointer group"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
          style={{
            backgroundColor: isBranch ? `${color}15` : "rgba(46,46,62,0.6)",
            border: `1px solid ${isBranch ? `${color}30` : "rgba(46,46,62,0.8)"}`,
          }}
        >
          <i className={`${QUESTION_ICONS[stat.questionId] || "ri-question-line"} text-sm`} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-white">{stat.label}</span>
            {isBranch && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-md font-medium border"
                style={{ color, borderColor: `${color}30`, backgroundColor: `${color}10` }}
              >
                Conditional Branch
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-silver-400">
              Shown: <span className="text-silver-200 font-medium">{stat.shownCount}</span>
              {isBranch && ` (${shownPct}% of sessions)`}
            </span>
            {stat.skipCount > 0 && (
              <span className="text-[11px] text-red-400">
                Skipped: <span className="font-medium">{stat.skipCount}</span> ({stat.skipRate}%)
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-graphite-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${answeredPctOfShown}%`, backgroundColor: color }}
            />
          </div>
          <span className="text-xs font-medium text-white w-8 text-right">
            {answeredPctOfShown}%
          </span>
          <i className={`ri-arrow-down-s-line text-silver-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {expanded && stat.answerBreakdown.length > 0 && (
        <div className="pb-3 pl-12 pr-2 space-y-2">
          <p className="text-[10px] text-silver-500 uppercase tracking-wider font-medium">Answer Distribution</p>
          {stat.answerBreakdown.map((row) => (
            <div key={row.value} className="flex items-center gap-2.5">
              <span className="text-[11px] text-silver-300 w-36 flex-shrink-0 truncate">{row.label}</span>
              <div className="flex-1 h-1.5 bg-graphite-700/60 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${row.pct}%`, backgroundColor: color }}
                />
              </div>
              <span className="text-[11px] text-silver-400 w-6 text-right">{row.count}</span>
              <span className="text-[10px] text-silver-500 w-8 text-right">{row.pct}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QuestionBranching({ sessions }: { sessions: AnalyticsSession[] }) {
  const data = getBranchingStats(sessions);

  const branchQuestions = ["complianceNeeds", "locationCount"];
  const coreQuestions = data.stats.filter((s) => !branchQuestions.includes(s.questionId));
  const branchStats = data.stats.filter((s) => branchQuestions.includes(s.questionId));

  return (
    <div className="glass-card p-4 md:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
        <div>
          <h3 className="text-base font-bold text-white">Question Flow &amp; Branching</h3>
          <p className="text-xs text-silver-400 mt-0.5">
            Which questions users see, which they skip, and where branches trigger
          </p>
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatBadge
          label={`${data.total} Total Sessions`}
          value={String(data.total)}
          color="#10b981"
        />
        <StatBadge
          label={`${data.completed} Completed`}
          value={String(data.completed)}
          color="#34d399"
        />
        <StatBadge
          label={`${data.avgQuestionsAnswered} Avg Questions`}
          value={String(data.avgQuestionsAnswered)}
          color="#0ea5e9"
        />
        <StatBadge
          label={`${data.branchHitRate}% Branch Hit Rate`}
          value={`${data.branchHitRate}%`}
          color="#f59e0b"
        />
      </div>

      {/* Branch trigger highlights */}
      <div className="flex flex-wrap gap-2 mb-4">
        {data.branchesHit.complianceNeeds > 0 && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-[11px] text-purple-400">
            <i className="ri-shield-check-line" />
            Compliance branch triggered {data.branchesHit.complianceNeeds} times
            <span className="text-silver-500">
              ({data.total > 0 ? Math.round((data.branchesHit.complianceNeeds / data.total) * 100) : 0}%)
            </span>
          </div>
        )}
        {data.branchesHit.locationCount > 0 && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-400">
            <i className="ri-map-pin-line" />
            Locations branch triggered {data.branchesHit.locationCount} times
            <span className="text-silver-500">
              ({data.total > 0 ? Math.round((data.branchesHit.locationCount / data.total) * 100) : 0}%)
            </span>
          </div>
        )}
      </div>

      {/* Core questions */}
      <div className="mb-1">
        <p className="text-[10px] text-silver-500 uppercase tracking-wider font-medium mb-1">Core Questions</p>
      </div>
      <div className="mb-4">
        {coreQuestions.map((stat) => (
          <QuestionRow
            key={stat.questionId}
            stat={stat}
            totalSessions={data.total}
            isBranch={false}
          />
        ))}
      </div>

      {/* Conditional branches */}
      <div className="mb-1">
        <p className="text-[10px] text-silver-500 uppercase tracking-wider font-medium mb-1">Conditional Branches</p>
      </div>
      <div>
        {branchStats.map((stat) => (
          <QuestionRow
            key={stat.questionId}
            stat={stat}
            totalSessions={data.total}
            isBranch={true}
          />
        ))}
      </div>
    </div>
  );
}